import { type FC, useCallback, useMemo, useState } from 'react';
import { Combobox } from './ui/combobox';
import { useUniqueValuesInDimension } from '../services/reportBuilder.service';

interface DimensionValueComboboxProps {
  /** Empty means no dimension picked yet — the list stays empty. */
  dimensionId: string;
  value: string;
  onValueChange: (value: string) => void;
  placeholder?: string;
  className?: string;
}

/**
 * Single-value picker for a dimension, backed by the same `/dimensions/:id/values`
 * search the Data Explorer's dimension filters use — type to filter server-side,
 * pick from the dropdown.
 *
 * Unlike a filter, a goal can legitimately name a value that isn't in the data
 * yet (next quarter's Month, a channel that hasn't launched), so whatever the
 * user types stays committable.
 */
export const DimensionValueCombobox: FC<DimensionValueComboboxProps> = ({
  dimensionId,
  value,
  onValueChange,
  placeholder = 'Select or type a value…',
  className,
}) => {
  const [query, setQuery] = useState('');

  const { data: dimensionValues, isLoading } = useUniqueValuesInDimension(
    dimensionId || null,
    query
  );

  const options = useMemo(
    () =>
      (dimensionValues || []).map((v) => ({
        value: v,
        label: v.length > 50 ? `${v.slice(0, 50)}…` : v,
      })),
    [dimensionValues]
  );

  // Stable identity: Combobox calls this from an effect keyed on the callback.
  const handleInputChange = useCallback((q: string) => setQuery(q), []);

  return (
    <Combobox
      options={options}
      value={value}
      onValueChange={onValueChange}
      onInputChange={handleInputChange}
      allowCustomValue
      isLoading={isLoading}
      placeholder={placeholder}
      emptyText="No matching values."
      className={className}
    />
  );
};
